import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Star } from "lucide-react";
import { BUSINESS } from "@/data/business";
import HeroCarousel from "./HeroCarousel";

const HERO_SLIDES = [
  { src: "/generated/hero_main.png", label: "Showroom", sublabel: "Nagawara, Bangalore" },
  { src: "/generated/hero_plywood.png", label: "Plywood", sublabel: "Commercial · Marine · BWP" },
  { src: "/generated/hero_laminates.png", label: "Laminates", sublabel: "1000+ Formica finishes" },
  { src: "/generated/hero_flushdoor.png", label: "Flush Doors", sublabel: "Solid core, veneered" },
  { src: "/generated/hero_veneer.png", label: "Veneers", sublabel: "Walnut · Teak · Rosewood" },
];

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 pb-20 sm:pb-28 bg-white overflow-hidden"
    >
      <div className="container-nitc grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
        {/* LEFT: copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="flex items-center gap-3 text-eyebrow"
          >
            <span className="h-px w-8 bg-[color:var(--nitc-accent)]" />
            Plywood &amp; Laminate Dealers · Since 1998
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="mt-7 font-display text-[color:var(--nitc-ink)] leading-[0.95] tracking-tight"
          >
            <span className="block text-[18vw] sm:text-[13vw] lg:text-[9.5vw] xl:text-[8.5rem] font-semibold">
              New India
            </span>
            <span className="block mt-1 text-lg sm:text-xl lg:text-2xl tracking-[0.18em] uppercase text-[color:var(--nitc-text)] font-medium">
              Trading Corporation
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-xl text-[color:var(--nitc-text)] text-base sm:text-lg leading-relaxed"
          >
            {BUSINESS.tagline}. For over {BUSINESS.yearsExperience}+ years, we have supplied premium plywood,
            laminates, flush doors and shuttering boards to builders, designers and packaging
            companies — at honest dealer pricing, across India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#enquiry"
              data-testid="hero-contact-button"
              className="group inline-flex items-center justify-center gap-2 bg-[color:var(--nitc-accent)] hover:bg-[color:var(--nitc-accent-hover)] text-white text-sm font-medium px-7 py-4 rounded-md transition-colors"
            >
              Contact Us
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              /* href={`https://wa.me/${BUSINESS.whatsappRaw}?text=${encodeURIComponent(
                "Hi New India Trading Corporation, I would like to enquire about your plywood products.",
              )}`} */
              href="#enquiry"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group inline-flex items-center justify-center gap-2 border border-[color:var(--nitc-ink)] hover:border-[color:var(--nitc-accent)] hover:text-[color:var(--nitc-accent)] text-[color:var(--nitc-ink)] text-sm font-medium px-7 py-4 rounded-md transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Enquiry
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.5 }}
            className="mt-12 flex items-center gap-6 text-[color:var(--nitc-muted)] text-xs sm:text-sm"
          >
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-[color:var(--nitc-accent)] text-[color:var(--nitc-accent)]" />
              ))}
              <span className="ml-2 text-[color:var(--nitc-text)]">Trusted by 5,000+ clients</span>
            </div>
            <span className="hidden sm:inline h-3 w-px bg-[color:var(--nitc-border)]" />
            <span className="hidden sm:inline">Pan India Delivery</span>
          </motion.div>
        </div>

        {/* RIGHT: image carousel */}
        <div className="lg:col-span-5">
          <HeroCarousel slides={HERO_SLIDES} interval={5000} />
        </div>
      </div>

      {/* Floating faded "TRUST" wordmark */}
      <div className="hidden lg:block pointer-events-none absolute -bottom-10 left-0 right-0 overflow-hidden">
        <p
          className="font-display text-[12rem] leading-none tracking-tighter font-semibold whitespace-nowrap text-center"
          style={{ color: "rgba(30,30,30,0.035)" }}
        >
          Strength · Quality · Trust · Strength · Quality · Trust
        </p>
      </div>
    </section>
  );
};

export default Hero;
